import { getNamedRangeFromWorksheet, getProjectReportData } from "../workbook";
import { Bid, BidGroup, InternalEstimateMarkup, ScopeUseGroup } from "../../interfaces/BidTabs";
import { getReportColumnCount, type ColumnNameToIndexMap, updateReportDate } from "./helpers/common";
import {
  PreservedContext,
  applyPreservedValuesToRow,
  getPreservedColumnValues,
  getPreservedKeyColumnIndices,
  shouldUsePreservedValues,
} from "./helpers/preservedColumns";
import { createColumnNameToIndexMap, printFormattedData } from "./helpers/print";
import { ValidationError } from "../../errors";
import { OVER_BUDGET_TEXT_COLOR, UNDER_BUDGET_TEXT_COLOR } from "../styles/colors";
import { columnNames, rangeNames, rowNames } from "./helpers/rangeNames";
import { getBidTabs } from "../datasources/bidTabs";
import { getLoadedContractingProjectId } from "../workbook/state";
import { transformBidTabsToFlatRows } from "../datasources/bidTabs";
import { track } from "../../clients/mixpanel";
import { worksheetCache } from "../workbook/cache";
import { ReportUseGroup } from "../../interfaces/EstimateUseGroup";
import { handleUseGroupsWithDenominators, UseGroupColumnInfo } from "./helpers/useGroups";
import {
  AlternateColumnInfo,
  BidColumnInfo,
  buildAlternateBidLookup,
  computeBuyStats,
  getTopBids,
  handleBidColumns,
  handleBuyStatsColumns,
  handleScopeAlternateColumns,
} from "./helpers/contractingColumns";
import { ensureStylesExist } from "../styles/worksheet";

export const fetchBidTabsWithScopeData = async (projectIdParam?: string): Promise<BidGroup[]> => {
  let projectId = projectIdParam;

  if (!projectId) {
    const reportData = (await getProjectReportData()) as any[];
    if (reportData.length === 0) {
      throw new ValidationError("No project loaded. Please select a project first.");
    }
    projectId = reportData[0].project_id as string;
  }

  // Gated on contracting's own marker, not the project's: this asks the server whether the project
  // has bid packages, and that answer holds whether or not the workbook has contracting loaded.
  if ((await getLoadedContractingProjectId()) !== projectId) return [];

  const { bidGroups } = await getBidTabs(projectId);
  return bidGroups || [];
};

/**
 * Build a mapping from scope_use_group.id → estimate_use_group.id
 * so contracting rows can map BidStats.useGroups (keyed by scope UG) into the
 * column layout (keyed by estimate UG).
 */
const buildScopeToEstimateUseGroupMap = (scopeUseGroups: ScopeUseGroup[]): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const sug of scopeUseGroups) {
    map[sug.id] = sug.estimate_use_group_id;
  }
  return map;
};

export const generateContractingSOV = async (worksheet: Excel.Worksheet) => {
  const startTime = performance.now();
  // Resolve projectId from .metadata (like fetchBidTabsWithScopeData and every other report), not
  // sessionStorage — the session value can lag the metadata during/after a project switch, loading
  // the previous project's bid tabs under the newly selected project.
  const reportData = (await getProjectReportData()) as any[];
  const projectId = reportData[0]?.project_id as string | undefined;
  if (!projectId) {
    throw new ValidationError("No project selected");
  }

  const { bidGroups, markups, scopeUseGroups, scopeAlternates, scopeAlternateBids, scopeTradeCleanupBids, bids } =
    await getBidTabs(projectId);
  if (!bidGroups || bidGroups.length === 0) {
    throw new ValidationError("No bid tabs were found for this project");
  }

  // Map scope use groups to ReportUseGroup format for handleUseGroupsWithDenominators
  // Use scope_use_group.id as the id (not estimate_use_group_id) to match the keys
  // in selectedBidder.useGroups which are keyed by scope_use_group.id
  const useGroups: ReportUseGroup[] = scopeUseGroups.map((sug) => ({
    id: sug.id,
    name: sug.label,
    parent_use_group: sug.use_group,
    gsf: sug.gsf,
    nsf: sug.nsf,
    order: sug.sort_order ?? 0,
    unit_cost_label: null,
    use_group: sug.use_group,
    use_group_id: sug.id,
    category: "",
    floor_group: "",
    costOfTradesSubtotal: 0,
    unit_count: sug.unit_count,
    exclude_area: false,
    room_area: 0,
    room_count: 0,
  }));
  useGroups.sort((a, b) => a.order - b.order);
  await ensureStylesExist();

  // Override _gsf with sum of scope use group GSF values instead of referencing __UseGroups
  const totalGsf = scopeUseGroups.reduce((sum, sug) => sum + (sug.gsf || 0), 0);
  const gsfRange = await getNamedRangeFromWorksheet(worksheet, rangeNames.gsf);
  if (gsfRange) {
    gsfRange.values = [[totalGsf]];
    await worksheet.context.sync();
  }

  // Set up dynamic columns: use groups first, then bids (bids are before use groups
  // in the template, so inserting bid columns shifts use groups right correctly)
  const useGroupsResult = await handleUseGroupsWithDenominators(worksheet, useGroups, rowNames.contractingSOV, true, {
    markupsRangeName: rowNames.contractingMarkups,
    subTotalsRangeNamePrefix: rowNames.subTotals,
    grandTotalRangeName: rowNames.grandTotal,
    applyStyleToFirstRow: true,
  });

  await handleBuyStatsColumns(worksheet);
  const bidColumnsResult = await handleBidColumns(worksheet, bidGroups, bids);
  const altColumnsResult = await handleScopeAlternateColumns(worksheet, scopeAlternates);

  const columnMap = await createColumnNameToIndexMap(worksheet);
  const usePreservedValues = await shouldUsePreservedValues(worksheet, columnMap, projectId, undefined);
  let preserved: PreservedContext | undefined;
  if (usePreservedValues) {
    const keyColumnIndices = getPreservedKeyColumnIndices(columnMap, "contracting");
    const values = await getPreservedColumnValues(worksheet, rowNames.contractingSOV, columnMap, keyColumnIndices);
    preserved = { values, keyColumnIndices };
  }

  const bidTabsData = transformBidTabsToFlatRows(
    bidGroups,
    scopeUseGroups,
    scopeAlternates,
    scopeAlternateBids,
    scopeTradeCleanupBids,
    bids
  );

  // Build scope→estimate use group mapping for row population
  const scopeToEstimateMap = buildScopeToEstimateUseGroupMap(scopeUseGroups);
  const costDenominatorsRowIndex = useGroupsResult?.costDenominatorsRowIndex;
  const bidColInfo = bidColumnsResult?.bidColumnInfo;
  const altColInfo = altColumnsResult?.alternateColumnInfo;

  // Build alternate bid lookup: bidGroupId → { alternateId → value }
  const selectedBidderIds: Record<string, string> = {};
  for (const bg of bidGroups) {
    const selected = Object.values(bg.totals).find((b) => b.selected);
    if (selected) selectedBidderIds[bg.id] = selected.id;
  }
  const altBidLookup = buildAlternateBidLookup(scopeAlternates, scopeAlternateBids, selectedBidderIds);

  const contractingSOV = contractingSOVarray(
    bidGroups,
    bidTabsData,
    columnMap,
    preserved,
    useGroups,
    scopeToEstimateMap,
    costDenominatorsRowIndex,
    bidColInfo,
    altColInfo,
    altBidLookup,
    bids
  );
  const startColumnIndex = columnMap.startColumnIndex;

  await printFormattedData({
    dataArray: contractingSOV,
    worksheet,
    insertAtRangeName: rowNames.contractingSOV,
    startColumnIndex,
  });

  // Apply per-row styling based on data
  const dataRange = await getNamedRangeFromWorksheet(worksheet, rowNames.contractingSOV);
  if (dataRange) {
    dataRange.load("rowIndex, rowCount");
    await worksheet.context.sync();
    const filteredBidGroups = bidGroups.filter((bg) => !bg.frontend && !bg.qual);
    const deltaColOffset = columnMap[columnNames.budgetDelta];
    const labelColOffset = columnMap.label;

    filteredBidGroups.forEach((bidGroup, i) => {
      const selectedBidder = Object.values(bidGroup.totals).find((b) => b.selected);
      const rowIndex = dataRange.rowIndex + i;
      const item = bidTabsData.find((r) => r.id === bidGroup.id);

      // Delta font color
      if (deltaColOffset !== undefined) {
        const delta = (selectedBidder?.grand_total || 0) - (bidGroup.budget || 0);
        if (delta !== 0) {
          worksheet.getCell(rowIndex, startColumnIndex + deltaColOffset).format.font.color =
            delta > 0 ? OVER_BUDGET_TEXT_COLOR : UNDER_BUDGET_TEXT_COLOR;
        }
      }

      // BoughtCell style for awarded bid groups
      if (item?.award === "true" && labelColOffset !== undefined) {
        worksheet.getCell(rowIndex, startColumnIndex + labelColOffset).style = "BoughtCell";
      }
    });
    await worksheet.context.sync();
  }

  await worksheetCache.setOptions(worksheet, {
    parentId: projectId,
  });

  if (await getNamedRangeFromWorksheet(worksheet, rowNames.contractingMarkups)) {
    let markupPreserved: PreservedContext | undefined;
    if (usePreservedValues) {
      const keyColumnIndices = getPreservedKeyColumnIndices(columnMap, "markups");
      const values = await getPreservedColumnValues(
        worksheet,
        rowNames.contractingMarkups,
        columnMap,
        keyColumnIndices
      );
      markupPreserved = { values, keyColumnIndices };
    }

    // Get subtotals and markups row indices for markup allocation formulas
    const subTotalsRange = await getNamedRangeFromWorksheet(worksheet, rowNames.subTotals);
    const markupsRange = await getNamedRangeFromWorksheet(worksheet, rowNames.contractingMarkups);
    subTotalsRange?.load("rowIndex");
    markupsRange?.load("rowIndex");
    await worksheet.context.sync();
    const subTotalsRowIndex = subTotalsRange?.rowIndex;
    const markupsFirstRowIndex = markupsRange?.rowIndex;

    const contractingMarkups = contractingMarkupsArray(
      markups,
      columnMap,
      markupPreserved,
      subTotalsRowIndex,
      markupsFirstRowIndex,
      useGroups,
      costDenominatorsRowIndex
    );
    await printFormattedData({
      dataArray: contractingMarkups || [],
      worksheet,
      insertAtRangeName: rowNames.contractingMarkups,
      startColumnIndex,
    });

    // Gray out data-only columns in markup rows (columns that map to bidTabRow keys
    // and bid columns — these don't have allocation formulas, so they should be grayed out)
    const updatedMarkupsRange = await getNamedRangeFromWorksheet(worksheet, rowNames.contractingMarkups);
    if (updatedMarkupsRange) {
      updatedMarkupsRange.load("rowIndex, rowCount");
      await worksheet.context.sync();
      const grayedOutKeys = new Set([
        "budget",
        "hold",
        "allowance",
        "trade_cleanup",
        "award",
        "projected_buy",
        "buy_date",
        "buy_value",
        "trade_estimator",
        "updated_by",
        "last_updated",
        "lock",
        "id",
        columnNames.budgetDelta,
      ]);
      const grayCols: number[] = [];
      for (const [key, offset] of Object.entries(columnMap)) {
        if (grayedOutKeys.has(key) || key.startsWith("_bid_")) {
          grayCols.push(offset);
        }
      }
      for (let r = 0; r < updatedMarkupsRange.rowCount; r++) {
        for (const col of grayCols) {
          worksheet.getCell(updatedMarkupsRange.rowIndex + r, startColumnIndex + col).style = "GrayedOutCell";
        }
      }
      await worksheet.context.sync();
    }
  }

  await updateReportDate(worksheet);

  track("excel.createReport.contractingSOV", {
    loadTime: (performance.now() - startTime) / 1000,
    hasUseGroups: useGroups.length > 0,
    useGroupCount: useGroups.length,
  });
};

const contractingSOVarray = (
  bidGroups: BidGroup[],
  bidTabsData: Record<string, any>[],
  columnMap: ColumnNameToIndexMap,
  preserved?: PreservedContext,
  useGroups: ReportUseGroup[] = [],
  scopeToEstimateMap: Record<string, string> = {},
  costDenominatorsRowIndex?: number,
  bidColInfo?: BidColumnInfo,
  altColInfo?: AlternateColumnInfo,
  altBidLookup?: Record<string, Record<string, number>>,
  bids: Bid[] = []
): (string | number | null)[][] => {
  const firstUseGroupColumnIndex = columnMap?.[columnNames.useGroups];
  const useGroupsRequested = firstUseGroupColumnIndex !== undefined;
  const costPerColumnsRequested = costDenominatorsRowIndex !== undefined;

  // Register bid columns in columnMap so getReportColumnCount accounts for them
  const firstBidColumnIndex = columnMap?.[columnNames.bids];
  if (bidColInfo && bidColInfo.length > 0 && firstBidColumnIndex !== undefined) {
    let colIdx = firstBidColumnIndex;
    for (let i = 0; i < bidColInfo.length; i++) {
      columnMap[`_bid_${i}_name`] = colIdx++;
      columnMap[`_bid_${i}_total`] = colIdx++;
    }
  }

  // Register buy stats columns in columnMap
  const firstBuyStatsIndex = columnMap?.[columnNames.buyStats];
  if (firstBuyStatsIndex !== undefined) {
    columnMap["_bs_projected_buy"] = firstBuyStatsIndex;
    columnMap["_bs_total_with_proj"] = firstBuyStatsIndex + 1;
    columnMap["_bs_buy_value"] = firstBuyStatsIndex + 2;
    columnMap["_bs_actual_buy"] = firstBuyStatsIndex + 3;
  }

  // Register alternate columns in columnMap
  const firstAltIndex = columnMap?.[columnNames.scopeAlternates];
  if (altColInfo && altColInfo.length > 0 && firstAltIndex !== undefined) {
    let colIdx = firstAltIndex;
    for (const alt of altColInfo) {
      columnMap[`_alt_${alt.alternateId}`] = colIdx++;
    }
  }

  // Build use-group column indices and register them in columnMap so
  // getReportColumnCount accounts for the extra columns (mirroring estimateSOVarray).
  const ugColIndices: UseGroupColumnInfo = {};
  if (useGroupsRequested && useGroups.length > 0) {
    let columnIndex = firstUseGroupColumnIndex;
    useGroups.forEach((useGroup) => {
      const totalCol = columnIndex++;
      columnMap[useGroup.id] = totalCol;

      let gsfCol: number | undefined;
      let unitCol: number | undefined;
      if (useGroup.gsf && useGroup.gsf > 0 && costPerColumnsRequested) gsfCol = columnIndex++;
      if (useGroup.unit_count && useGroup.unit_count > 0 && costPerColumnsRequested) unitCol = columnIndex++;
      ugColIndices[useGroup.id] = { total: totalCol, gsf: gsfCol, unit: unitCol };
    });
  }

  // Build a lookup from bid group id → __BidTabs row
  const bidTabsByGroupId: Record<string, Record<string, any>> = {};
  for (const row of bidTabsData) {
    if (row.id) bidTabsByGroupId[row.id] = row;
  }

  const columnCount = getReportColumnCount(columnMap);
  const filteredBidGroups = bidGroups.filter((bidGroup) => !bidGroup.frontend && !bidGroup.qual);
  const dataArray = filteredBidGroups.map((bidGroup) => {
    const row: (string | number | null)[] = Array(columnCount).fill(null);
    const selectedBidder = Object.values(bidGroup.totals).find((bidder) => bidder.selected);

    // Read the pre-computed item from __BidTabs (populated by tabulateBidTabs)
    const item = bidTabsByGroupId[bidGroup.id] || {};

    // Formula columns keyed by columnNames
    const formulaColumns: Record<string, () => string | null> = {
      [columnNames.budgetDelta]: () => {
        if (columnMap.budget === undefined || columnMap.total === undefined) return null;
        const colIndex = columnMap[columnNames.budgetDelta];
        return `=IFERROR(RC[${columnMap.total - colIndex}]-RC[${columnMap.budget - colIndex}],"")`;
      },
      [columnNames.costPerGSF]: () => {
        if (columnMap.total === undefined) return null;
        const colIndex = columnMap[columnNames.costPerGSF];
        return `=IFERROR(RC[${columnMap.total - colIndex}]/${rangeNames.gsf},"")`;
      },
      [columnNames.budgetPerGSF]: () => {
        if (columnMap.budget === undefined) return null;
        const colIndex = columnMap[columnNames.budgetPerGSF];
        return `=IFERROR(RC[${columnMap.budget - colIndex}]/${rangeNames.gsf},"")`;
      },
      [columnNames.budgetDeltaPerGSF]: () => {
        if (columnMap[columnNames.budgetDelta] === undefined) return null;
        const colIndex = columnMap[columnNames.budgetDeltaPerGSF];
        return `=IFERROR(RC[${columnMap[columnNames.budgetDelta] - colIndex}]/${rangeNames.gsf},"")`;
      },
    };

    // Auto-map: iterate columnMap keys, skip dynamic columns (use groups, bids, buy stats, alternates)
    Object.keys(columnMap).forEach((key) => {
      const isDynamicColumn =
        key === "startColumnIndex" ||
        key === columnNames.useGroups ||
        key === columnNames.bids ||
        key === columnNames.buyStats ||
        key === columnNames.scopeAlternates ||
        key.startsWith("_bid_") ||
        key.startsWith("_bs_") ||
        key.startsWith("_alt_") ||
        key.match(/^[0-9a-f]{8}-[0-9a-f]{4}-/);

      if (!isDynamicColumn) {
        if (key in formulaColumns) {
          const formula = formulaColumns[key]();
          if (formula) row[columnMap[key]] = formula;
        } else if (key in item) {
          row[columnMap[key]] = item[key] as string | number | null;
        }
      }
    });

    if (preserved) {
      applyPreservedValuesToRow(row, columnMap, preserved);
    }

    // Populate bid columns (up to 3 bids: name + grand_total)
    if (bidColInfo && bidColInfo.length > 0 && firstBidColumnIndex !== undefined) {
      const topBids = getTopBids(bidGroup, bids);
      let colIdx = firstBidColumnIndex;
      for (let i = 0; i < bidColInfo.length; i++) {
        const bid = topBids[i];
        row[colIdx++] = bid?.name || "";
        row[colIdx++] = bid?.grand_total || 0;
      }
    }

    // Populate buy stats columns
    if (firstBuyStatsIndex !== undefined) {
      const stats = computeBuyStats(bidGroup, selectedBidder);
      row[firstBuyStatsIndex] = stats.projectedBuy;
      row[firstBuyStatsIndex + 1] = stats.totalWithProjectedBuy;
      row[firstBuyStatsIndex + 2] = stats.buyValue;
      row[firstBuyStatsIndex + 3] = stats.actualBuy;
    }

    // Populate use group columns from the selected bidder's use group allocations
    if (useGroupsRequested && useGroups.length > 0) {
      // Resolve per-estimate-use-group totals from the bidder's useGroups data.
      // The keys may be estimate_use_group_ids (direct match) or scope_use_group_ids
      // (need mapping). Try direct first, then fall back to mapping.
      const bidderUseGroups = selectedBidder?.useGroups || {};
      const estimateUgTotals: Record<string, number> = {};

      for (const [key, value] of Object.entries(bidderUseGroups)) {
        // Check if the key directly matches an estimate use group id
        const isDirectMatch = useGroups.some((ug) => ug.id === key);
        if (isDirectMatch) {
          estimateUgTotals[key] = (estimateUgTotals[key] || 0) + value;
        } else {
          // Fall back to scope→estimate mapping
          const estimateUgId = scopeToEstimateMap[key];
          if (estimateUgId) {
            estimateUgTotals[estimateUgId] = (estimateUgTotals[estimateUgId] || 0) + value;
          }
        }
      }

      useGroups.forEach((useGroup) => {
        const colInfo = ugColIndices[useGroup.id];
        if (!colInfo) return;

        const ugTotal = estimateUgTotals[useGroup.id] || 0;
        row[colInfo.total] = ugTotal;

        if (colInfo.gsf !== undefined && costPerColumnsRequested) {
          row[colInfo.gsf] = `=IFERROR(RC[-1]/R${costDenominatorsRowIndex! + 1}C,0)`;
        }
        if (colInfo.unit !== undefined && costPerColumnsRequested) {
          const offset = colInfo.gsf !== undefined ? -2 : -1;
          row[colInfo.unit] = `=IFERROR(RC[${offset}]/R${costDenominatorsRowIndex! + 1}C,0)`;
        }
      });
    } else if (useGroupsRequested && useGroups.length === 0) {
      // Named range exists but no use groups — write empty string to clear old values
      row[firstUseGroupColumnIndex] = "";
    }

    // Populate alternate columns
    if (altColInfo && altColInfo.length > 0 && firstAltIndex !== undefined && altBidLookup) {
      const bgAlts = altBidLookup[bidGroup.id] || {};
      let colIdx = firstAltIndex;
      for (const alt of altColInfo) {
        row[colIdx++] = bgAlts[alt.alternateId] || 0;
      }
    } else if (firstAltIndex !== undefined && (!altColInfo || altColInfo.length === 0)) {
      row[firstAltIndex] = "";
    }

    return row;
  });
  return dataArray;
};

const contractingMarkupsArray = (
  markups: InternalEstimateMarkup[],
  columnMap: ColumnNameToIndexMap,
  preserved?: PreservedContext,
  subTotalsRowIndex?: number,
  _markupsFirstRowIndex?: number,
  useGroups: ReportUseGroup[] = [],
  costDenominatorsRowIndex?: number
) => {
  markups.sort((a, b) => (a.order || 0) - (b.order || 0));
  const columnCount = getReportColumnCount(columnMap);

  // Build use group column indices for $/SF and $/unit formulas
  const firstUseGroupColumnIndex = columnMap?.[columnNames.useGroups];
  const costPerColumnsRequested = costDenominatorsRowIndex !== undefined;
  const ugColIndices: UseGroupColumnInfo = {};
  if (firstUseGroupColumnIndex !== undefined && useGroups.length > 0) {
    let colIdx = firstUseGroupColumnIndex;
    useGroups.forEach((useGroup) => {
      const totalCol = colIdx++;
      let gsfCol: number | undefined;
      let unitCol: number | undefined;
      if (useGroup.gsf && useGroup.gsf > 0 && costPerColumnsRequested) gsfCol = colIdx++;
      if (useGroup.unit_count && useGroup.unit_count > 0 && costPerColumnsRequested) unitCol = colIdx++;
      ugColIndices[useGroup.id] = { total: totalCol, gsf: gsfCol, unit: unitCol };
    });
  }

  // Collect all column offsets that need markup allocation formulas
  // Includes: buy stats, use groups, scope alternates (NOT bids, holds, allowances, trade_cleanup)
  const dynamicColOffsets: number[] = [];
  const dynamicColumnNames = [columnNames.buyStats, columnNames.useGroups, columnNames.scopeAlternates];
  for (const name of dynamicColumnNames) {
    const offset = columnMap[name];
    if (offset !== undefined) dynamicColOffsets.push(offset);
  }
  // Also include registered sub-columns (_bs_*, _alt_*, use group ids — but NOT _bid_*)
  for (const [key, offset] of Object.entries(columnMap)) {
    if (key.startsWith("_bs_") || key.startsWith("_alt_")) {
      dynamicColOffsets.push(offset);
    }
    // Use group columns are registered by their scope_use_group id (UUID format)
    if (key.match(/^[0-9a-f]{8}-[0-9a-f]{4}-/)) {
      dynamicColOffsets.push(offset);
    }
  }

  const totalColOffset = columnMap[columnNames.total];

  const dataArray = markups.map((markup) => {
    const row: (string | number | null)[] = Array(columnCount).fill(null);
    Object.entries(columnMap).forEach(([prop, colIndex]) => {
      switch (prop) {
        case "name":
        case "description":
        case "label":
          row[colIndex] = markup?.description || "";
          break;
        case columnNames.markupPercentages:
          const markupPercentage = markup?.formula?.overridden ? "" : markup?.percentage / 100 || "";
          row[colIndex] = markupPercentage;
          break;
        case columnNames.total:
          const markupValue = markup?.formula?.overridden
            ? markup?.formula?.total || 0
            : (markup?.formula?.total || 0) * (markup?.percentage / 100 || 0);
          row[colIndex] = markupValue;
          break;
        case columnNames.costPerGSF:
          if (columnMap.total) {
            const colOffset = columnMap.total - colIndex;
            const formula = `=IFERROR(RC[${colOffset}]/${rangeNames.gsf},"")`;
            row[colIndex] = formula;
          }
          break;
      }
    });

    // Populate dynamic columns with allocation formula (absolute R1C1):
    // = (subtotal_same_col / subtotal_total_col) * markup_total
    if (subTotalsRowIndex !== undefined && totalColOffset !== undefined) {
      const subTotalsRow = subTotalsRowIndex + 1; // 1-indexed
      const totalAbsCol = totalColOffset + columnMap.startColumnIndex + 1;
      for (const colOffset of dynamicColOffsets) {
        const absCol = colOffset + columnMap.startColumnIndex + 1;
        row[colOffset] = `=IFERROR(R${subTotalsRow}C${absCol}/R${subTotalsRow}C${totalAbsCol}*RC${totalAbsCol},"")`;
      }
    }

    // Write $/SF and $/unit formulas for use group columns in markup rows
    if (useGroups.length > 0 && costPerColumnsRequested) {
      useGroups.forEach((useGroup) => {
        const colInfo = ugColIndices[useGroup.id];
        if (!colInfo) return;

        if (colInfo.gsf !== undefined) {
          row[colInfo.gsf] = `=IFERROR(RC[-1]/R${costDenominatorsRowIndex! + 1}C,0)`;
        }
        if (colInfo.unit !== undefined) {
          const offset = colInfo.gsf !== undefined ? -2 : -1;
          row[colInfo.unit] = `=IFERROR(RC[${offset}]/R${costDenominatorsRowIndex! + 1}C,0)`;
        }
      });
    }

    if (preserved) {
      applyPreservedValuesToRow(row, columnMap, preserved);
    }

    return row;
  });
  return dataArray;
};
